import React from 'react';
import {
  Box, Grid, makeStyles,
} from '@material-ui/core';
import Container from '#web-components/components/Container';
import Typography from '#web-components/components/Typography';
import type { TypographyProps } from '#web-components/components/Typography';
import Loader from '#web-components/components/Loader';
import TopBar from '#web-components/components/Layouts/components/TopBar';
import InlineButton from '#web-components/components/InlineButton';
import { ArrowLeftIcon, InfoIcon } from '#web-components/components/Icons';
import UnsupportedBrowserNotification from '#web-components/components/UnsupportedBrowserNotification';
import Link from '#web-components/components/Link';

import styles from './StandardLayout.styles';

export interface StandardLayoutProps {
  title: string;
  titleVariant?: string;
  sidebar: {
    logo: React.ReactNode;
    navLinks?: React.ReactNode;
    menu?: React.ReactNode;
  };
  backLink?: {
    title: string,
    path: string,
    RouterNavLink: React.ElementType,
  };
  children: React.ReactNode;
  description?: string;
  registryName?: string;
  registryFullName?: string;
  isLoading?: boolean;
  loaderDescription?: string;
  loaderIcon: React.ReactNode;
  t?: (key: string) => string;
  supportChannelUrl?: string;
}

const useStyles = makeStyles(styles, { name: 'StandardLayout' });

export default function StandardLayout(props: StandardLayoutProps) {
  const {
    title,
    titleVariant = 'h1',
    sidebar,
    children,
    registryName,
    registryFullName,
    description,
    backLink,
    isLoading = false,
    loaderDescription,
    loaderIcon,
    supportChannelUrl,
    t = (key: string) => key,
  } = props;

  const classes = useStyles(props);
  const isVisibleRegistryName = registryName && registryFullName;

  return (
    <Container spacing={0}>
      <UnsupportedBrowserNotification t={t} />
      <Box className={classes.main}>
        <Loader
          show={isLoading}
          description={loaderDescription}
          loaderIcon={loaderIcon}
          data-xpath="component-loader"
        />
        <Grid wrap="nowrap" container>
          <Grid item className={classes.sidebar}>
            <Container>
              <Box className={classes.sidebarContent}>
                <Box className={classes.logo}>
                  {sidebar.logo}
                </Box>
                <Box className={classes.navLinks}>
                  {sidebar.navLinks}
                </Box>
                {sidebar.menu}
              </Box>
            </Container>
          </Grid>
          <Grid item className={classes.content}>
            <Box>
              {isVisibleRegistryName && (
                <Container classes={{ root: classes.headerContainer }}>
                  <TopBar name={registryName} fullName={registryFullName} />
                  {supportChannelUrl && (
                    <Link to={supportChannelUrl} className={classes.supportLink} target="_blank">
                      <Typography variant="caption">{t('supportChannel.link.label')}</Typography>
                      <InfoIcon />
                    </Link>
                  )}
                </Container>
              )}
            </Box>
            <Box className={classes.body}>
              <Container spacing={6}>
                {
                  backLink && (
                    <InlineButton
                      size="small"
                      leftIcon={<ArrowLeftIcon />}
                      component={backLink.RouterNavLink}
                      to={backLink.path}
                      classes={{ link: classes.link }}
                    >
                      {backLink.title}
                    </InlineButton>
                  )
                }
                <Box className={classes.title}>
                  <Typography
                    variant={titleVariant as TypographyProps['variant']}
                    component="h1"
                    className={classes.h1}
                  >
                    {title}
                  </Typography>
                  {description
                    && <Typography variant="textRegular" className={classes.description}>{description}</Typography>}
                </Box>
                {children}
              </Container>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
